import React from 'react';
import { ClassValue } from 'classnames/types';
import styles from './Typography.module.scss';
import Element, { CommonProps } from '../Element/Element';
import { ArrayElementType } from '../../types/utils/ArrayElementType';

export type TypographyProps<T extends SupportedTagName = 'span'> = {
  /** HTML tag name */
  tagName?: T;
  /** Force text to one row and cut with ellipsis */
  singleLine?: boolean;
  /** Convert text to uppercase */
  uppercase?: boolean;
  /** Strike through over the text */
  strikeThrough?: boolean;
  /** Customizable color */
  color?: SupportedTextColor;
  /** Font */
  font?: SupportedFont;
  /** Allow passing fontweight of normal, medium, bold */
  fontWeight?: SupportedFontWeight;
} & Omit<CommonProps<T>, 'fontWeight' | 'color'>;

function Typography<T extends SupportedTagName>({
  tagName,
  font = 'teliasans',
  children,
  singleLine,
  uppercase,
  strikeThrough,
  className,
  fontWeight = 'normal',
  margin,
  block,
  ...props
}: TypographyProps<T>) {
  const classNames: ClassValue = [
    styles.typography,
    styles[`typography__${font}`],
    {
      [styles.typography__singleLine]: !!singleLine,
      [styles.typography__uppercase]: !!uppercase,
      [styles.typography__strikeThrough]: !!strikeThrough,
    },
    className,
  ];

  return (
    <Element
      tagName={(tagName ?? 'span') as SupportedTagName} // this circumvents some weird ts problem
      className={classNames}
      fontWeight={font === 'teliasans' ? fontWeight : 'normal'}
      margin={margin}
      block={!!margin || block}
      {...props}
    >
      {children}
    </Element>
  );
}

export const supportedTextColors = [
  'orange800',
  'purple500',
  'purple400',
  'black',
  'white',
  'gray400',
  'gray500',
  'gray700',
  'gray800',
  'red400',
  'red600',
] as const;

export type SupportedTextColor = ArrayElementType<typeof supportedTextColors>;

export const supportedFontWeights = ['normal', 'medium', 'bold'] as const;

export type SupportedFontWeight = ArrayElementType<typeof supportedFontWeights>;

export const supportedTagNames = [
  'label',
  'span',
  'div',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;

export type SupportedTagName = ArrayElementType<typeof supportedTagNames>;

export const supportedFonts = [
  'teliasans',
  'teliasansHeading',
  'pebble',
] as const;

export type SupportedFont = ArrayElementType<typeof supportedFonts>;

export default Typography;
